import { LitElement, html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { createRef, ref, Ref } from 'lit/directives/ref.js'
import '@material/web/dialog/dialog.js'
import '@material/web/button/text-button.js'
import '@material/web/button/filled-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import '@material/web/icon/icon.js'
import '@material/web/switch/switch.js'
import type { MdDialog } from '@material/web/dialog/dialog.js'
import type { MdOutlinedTextField } from '@material/web/textfield/outlined-text-field.js'
import type { MdOutlinedSelect } from '@material/web/select/outlined-select.js'
import maplibregl from 'maplibre-gl'
import { importService } from '../../services/import/import-service.js'
import type { ParsedImport } from '../../services/import/import-parser.js'
import { layerGroupManager, type LayerTreeNode } from '../../services/layer-group-manager.js'
import { db } from '../../database/schema.js'
import type { Plan } from '../../database/schema.js'

type ImportMode = 'layer' | 'existing-plan' | 'new-plan'

export interface ImportResult {
  layerName: string
  layerGroupId: string // only meaningful in 'layer' mode
  createPlan: boolean
  planId: string | undefined
  newPlanName: string | undefined
  parsed: ParsedImport
  importWaypoints: boolean
  createActivity: boolean
  /** Raw file contents for lossless preservation */
  rawData: string
  metadata: ParsedImport['metadata']
}

@customElement('import-dialog')
export class ImportDialog extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      z-index: 3000;
      pointer-events: none;
    }

    md-dialog {
      --md-dialog-container-color: #1e293b;
      --md-dialog-headline-color: white;
      --md-dialog-supporting-text-color: rgba(255, 255, 255, 0.8);
      --md-sys-color-outline: rgba(255, 255, 255, 0.2);
      --md-dialog-container-shape: 12px;
      pointer-events: all;
      max-width: 520px;
      width: 90vw;
    }

    @media (max-width: 600px) {
      md-dialog {
        max-width: 100vw;
        width: 100vw;
        --md-dialog-container-shape: 0;
      }
    }

    md-text-button {
      --md-text-button-label-text-color: rgba(255, 255, 255, 0.7);
      --md-text-button-hover-label-text-color: white;
    }

    md-filled-button {
      --md-filled-button-container-color: #3b82f6;
      --md-filled-button-label-text-color: white;
    }

    md-filled-button[disabled] {
      opacity: 0.4;
    }

    md-outlined-text-field {
      --md-outlined-text-field-input-text-color: white;
      --md-outlined-text-field-label-text-color: rgba(255, 255, 255, 0.7);
      --md-outlined-text-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-text-field-focus-outline-color: #3b82f6;
      --md-outlined-text-field-hover-outline-color: rgba(255, 255, 255, 0.5);
      --md-outlined-text-field-caret-color: #3b82f6;
      --md-outlined-text-field-focus-label-text-color: #3b82f6;
      width: 100%;
    }

    md-outlined-select {
      --md-outlined-select-text-field-input-text-color: white;
      --md-outlined-select-text-field-label-text-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-select-text-field-focus-outline-color: #3b82f6;
      --md-outlined-select-text-field-hover-outline-color: rgba(255, 255, 255, 0.5);
      --md-outlined-select-text-field-focus-label-text-color: #3b82f6;
      --md-menu-container-color: #1e293b;
      --md-menu-item-label-text-color: white;
      width: 100%;
    }

    .content {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .mode-row {
      display: flex;
      gap: 8px;
    }

    .mode-btn {
      flex: 1;
      padding: 10px 8px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      background: transparent;
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      cursor: pointer;
      transition: all 0.15s;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }

    .mode-btn:hover {
      border-color: rgba(255, 255, 255, 0.4);
      color: white;
    }

    .mode-btn.active {
      border-color: #3b82f6;
      background: rgba(59, 130, 246, 0.15);
      color: #3b82f6;
    }

    .mode-btn .material-icons {
      font-size: 20px;
    }

    .mode-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    .mode-detail {
      padding: 4px 0 0;
    }

    .error-message {
      color: #ef4444;
      font-size: 13px;
      padding: 8px 12px;
      background: rgba(239, 68, 68, 0.1);
      border-radius: 6px;
    }

    .preview-map {
      width: 100%;
      height: 180px;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .preview-map-container {
      width: 100%;
      height: 100%;
    }

    .metadata-summary {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 8px;
      padding: 12px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      font-size: 13px;
    }

    .meta-item {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .meta-label {
      color: rgba(255, 255, 255, 0.5);
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .import-options {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
    }

    .import-option-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 36px;
    }

    .import-option-label {
      color: rgba(255, 255, 255, 0.9);
      font-size: 13px;
    }

    .import-option-detail {
      color: rgba(255, 255, 255, 0.5);
      font-size: 11px;
      padding-left: 2px;
    }

    md-switch {
      --md-switch-selected-track-color: #3b82f6;
      --md-switch-selected-handle-color: white;
      --md-switch-selected-icon-color: #3b82f6;
      --md-switch-unselected-track-color: rgba(255, 255, 255, 0.15);
      --md-switch-unselected-handle-color: rgba(255, 255, 255, 0.5);
      flex-shrink: 0;
    }

    .meta-value {
      color: white;
      font-weight: 500;
    }

    /* Material icons inline */
    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      font-size: 24px;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      -webkit-font-smoothing: antialiased;
    }
  `

  @state() private parsed: ParsedImport | null = null
  @state() private importWaypoints = true
  @state() private createActivity = true
  private rawData = ''
  @state() private layerName = ''
  @state() private importMode: ImportMode = 'layer'
  @state() private selectedLayerGroupId = ''
  @state() private selectedPlanId = ''
  @state() private errorMessage = ''
  @state() private plans: Plan[] = []
  @state() private layerTree: LayerTreeNode[] = []
  /** When set, the dialog is locked to importing into this plan. */
  private contextPlanId: string | undefined

  private resolvePromise: ((result: ImportResult | null) => void) | undefined = undefined
  private animationsConfigured = false
  private previewMapRef: Ref<HTMLDivElement> = createRef()
  private previewMap: maplibregl.Map | undefined = undefined

  private configureAnimations(): void {
    if (this.animationsConfigured) {
      return
    }
    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) {
      return
    }
    this.animationsConfigured = true

    const dur = 100
    const easing = 'cubic-bezier(0.2, 0, 0, 1)'

    dialog.getOpenAnimation = () => ({
      dialog: [
        [
          [
            { transform: 'scale(0.85)', opacity: 0 },
            { transform: 'scale(1)', opacity: 1 },
          ],
          { duration: dur, easing },
        ],
      ],
      scrim: [[[{ opacity: 0 }, { opacity: 0.32 }], { duration: dur, easing: 'linear' }]],
      container: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur * 0.5, easing: 'linear', pseudoElement: '::before' },
        ],
      ],
      headline: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      content: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      actions: [
        [
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: dur, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
    })

    dialog.getCloseAnimation = () => ({
      dialog: [
        [
          [
            { transform: 'scale(1)', opacity: 1 },
            { transform: 'scale(0.9)', opacity: 0 },
          ],
          { duration: 80, easing: 'cubic-bezier(0.4, 0, 1, 1)' },
        ],
      ],
      scrim: [[[{ opacity: 0.32 }, { opacity: 0 }], { duration: 80, easing: 'linear' }]],
      container: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', pseudoElement: '::before' },
        ],
      ],
      headline: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      content: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
      actions: [
        [
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 50, easing: 'linear', fill: 'forwards' as FillMode },
        ],
      ],
    })
  }

  protected override firstUpdated(): void {
    this.configureAnimations()
  }

  async show(file: File, context?: { planId?: string }): Promise<ImportResult | null> {
    this.errorMessage = ''
    this.contextPlanId = context?.planId

    if (this.contextPlanId) {
      this.importMode = 'existing-plan'
      this.selectedPlanId = this.contextPlanId
    } else {
      this.importMode = 'layer'
      this.selectedPlanId = ''
    }

    try {
      this.rawData = await file.text()
      this.parsed = await importService.parseFile(file)
      this.importWaypoints = true
      this.createActivity = this.parsed.metadata.hasTrackTimes
    } catch (err) {
      this.errorMessage = `Failed to parse file: ${(err as Error).message}`
      this.parsed = null
      this.rawData = ''
    }

    this.layerName =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty name should fall through
      this.parsed?.metadata.name || file.name.replace(/\.\w+$/i, '') || 'Imported file'

    await this.loadData()

    // Auto-select first non-trip layer group
    const nonTripGroups = this.getNonTripGroups()
    if (nonTripGroups.length > 0) {
      this.selectedLayerGroupId = nonTripGroups[0].id
    }

    this.configureAnimations()
    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (!dialog) {
      return null
    }

    // Load MapLibre CSS into shadow root
    try {
      const response = await fetch('/node_modules/maplibre-gl/dist/maplibre-gl.css')
      const cssText = await response.text()
      if (!this.shadowRoot?.querySelector('#maplibre-css')) {
        const style = document.createElement('style')
        style.id = 'maplibre-css'
        style.textContent = cssText
        this.shadowRoot?.appendChild(style)
      }
    } catch (_) {
      /* ok in prod */
    }

    this.requestUpdate()
    await this.updateComplete

    return new Promise<ImportResult | null>(resolve => {
      this.resolvePromise = resolve
      void dialog.show()
      setTimeout(() => this.initPreviewMap(), 150)
    })
  }

  private async loadData(): Promise<void> {
    this.layerTree = await layerGroupManager.getLayerTree()
    this.plans = await db.plans.orderBy('updatedAt').reverse().toArray()
  }

  /** Get non-trip layer groups suitable for standalone import */
  private getNonTripGroups(): { id: string; name: string; depth: number }[] {
    const result: { id: string; name: string; depth: number }[] = []
    const walk = (nodes: LayerTreeNode[], depth: number): void => {
      for (const node of nodes) {
        // Skip trip system groups (trip, tracks, features under a trip)
        if (node.layer.tripId) {
          continue
        }
        result.push({ id: node.layer.id, name: node.layer.name, depth })
        if (node.children.length > 0) {
          walk(node.children, depth + 1)
        }
      }
    }
    walk(this.layerTree, 0)
    return result
  }

  private handleClose(e: Event): void {
    const dialog = e.target as MdDialog

    if (this.previewMap) {
      this.previewMap.remove()
      this.previewMap = undefined
    }

    if (dialog.returnValue === 'import' && this.parsed) {
      const result: ImportResult = {
        layerName: this.layerName,
        layerGroupId: this.selectedLayerGroupId,
        createPlan: this.importMode === 'new-plan',
        parsed: this.parsed,
        importWaypoints: this.importWaypoints,
        createActivity: this.createActivity,
        rawData: this.rawData,
        metadata: this.parsed.metadata,
        planId: this.importMode === 'existing-plan' ? this.selectedPlanId : undefined,
        newPlanName: this.importMode === 'new-plan' ? this.layerName : undefined,
      }
      this.resolvePromise?.(result)
    } else {
      this.resolvePromise?.(null)
    }
    this.resolvePromise = undefined
  }

  private close(returnValue: string): void {
    const dialog = this.shadowRoot?.querySelector('md-dialog') as MdDialog | null
    if (dialog) {
      void dialog.close(returnValue)
    }
  }

  private initPreviewMap(): void {
    if (!this.parsed || !this.previewMapRef.value) {
      return
    }
    if (this.previewMap) {
      this.previewMap.remove()
      this.previewMap = undefined
    }

    this.previewMap = new maplibregl.Map({
      container: this.previewMapRef.value,
      style: {
        version: 8,
        sources: {
          'osm-tiles': {
            type: 'raster',
            tiles: [
              'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
              'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            maxzoom: 18,
          },
        },
        layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm-tiles' }],
      },
      center: [-104.99, 39.74],
      zoom: 10,
      interactive: false,
      attributionControl: false,
    })

    this.previewMap.on('load', (): void => this.addTrackToPreviewMap())
  }

  private addTrackToPreviewMap(): void {
    if (!this.previewMap || !this.parsed) {
      return
    }

    this.previewMap.addSource('import-track', {
      type: 'geojson',
      data: this.parsed.allFeatures,
    })

    this.previewMap.addLayer({
      id: 'import-track-line',
      type: 'line',
      source: 'import-track',
      filter: ['in', '$type', 'LineString'],
      paint: { 'line-color': '#3b82f6', 'line-width': 3, 'line-opacity': 0.9 },
    })

    this.previewMap.addLayer({
      id: 'import-track-points',
      type: 'circle',
      source: 'import-track',
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 5,
        'circle-color': '#f59e0b',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    })

    const meta = this.parsed.metadata
    if (meta.bounds) {
      const { sw, ne } = meta.bounds
      this.previewMap.fitBounds(
        [
          [sw[1], sw[0]],
          [ne[1], ne[0]],
        ],
        { padding: 30, duration: 0 }
      )
    }
  }

  private canImport(): boolean {
    if (!this.parsed) {
      return false
    }
    if (!this.layerName.trim()) {
      return false
    }
    if (this.importMode === 'layer' && !this.selectedLayerGroupId) {
      return false
    }
    if (this.importMode === 'existing-plan' && !this.selectedPlanId) {
      return false
    }
    if (this.importMode === 'new-plan' && !this.layerName.trim()) {
      return false
    }
    return true
  }

  private renderModeDetail(): unknown {
    const nonTripGroups = this.getNonTripGroups()

    switch (this.importMode) {
      case 'layer':
        return html`
          <md-outlined-select
            label="Layer group"
            .value=${this.selectedLayerGroupId}
            @change=${(e: Event): void => {
              this.selectedLayerGroupId = (e.target as MdOutlinedSelect).value
            }}
          >
            ${nonTripGroups.map(
              g => html`
                <md-select-option value=${g.id}>
                  <div slot="headline">${'\u00A0'.repeat(g.depth * 2)}${g.name}</div>
                </md-select-option>
              `
            )}
          </md-outlined-select>
        `
      case 'existing-plan':
        return html`
          <md-outlined-select
            label="Select plan"
            .value=${this.selectedPlanId}
            ?disabled=${!!this.contextPlanId}
            @change=${(e: Event): void => {
              this.selectedPlanId = (e.target as MdOutlinedSelect).value
            }}
          >
            ${this.plans.map(
              p => html`
                <md-select-option value=${p.id}>
                  <div slot="headline">${p.name}</div>
                </md-select-option>
              `
            )}
          </md-outlined-select>
        `
      case 'new-plan':
        return html``
    }
  }

  private renderImportOptions(): unknown {
    if (!this.parsed) return ''
    const meta = this.parsed.metadata
    const isGpx = meta.sourceFormat === 'gpx'
    const hasWaypoints = isGpx && meta.waypointCount > 0
    const hasTrackTimes = isGpx && meta.hasTrackTimes

    if (!hasWaypoints && !hasTrackTimes) return ''

    return html`
      <div class="import-options">
        ${hasWaypoints
          ? html`
              <div class="import-option-row">
                <div>
                  <div class="import-option-label">Import waypoints (${meta.waypointCount})</div>
                </div>
                <md-switch
                  ?selected=${this.importWaypoints}
                  @change=${(e: Event): void => {
                    this.importWaypoints = (
                      e.target as HTMLInputElement & { selected: boolean }
                    ).selected
                  }}
                ></md-switch>
              </div>
            `
          : ''}
        ${hasTrackTimes
          ? html`
              <div class="import-option-row">
                <div>
                  <div class="import-option-label">Create activity from recording</div>
                  <div class="import-option-detail">
                    ${meta.trackStartTime
                      ? new Date(meta.trackStartTime).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : ''}
                    ${meta.trackStartTime && meta.trackEndTime ? ' – ' : ''}
                    ${meta.trackEndTime
                      ? new Date(meta.trackEndTime).toLocaleTimeString(undefined, {
                          hour: 'numeric',
                          minute: '2-digit',
                        })
                      : ''}
                  </div>
                </div>
                <md-switch
                  ?selected=${this.createActivity}
                  @change=${(e: Event): void => {
                    this.createActivity = (
                      e.target as HTMLInputElement & { selected: boolean }
                    ).selected
                  }}
                ></md-switch>
              </div>
            `
          : ''}
      </div>
    `
  }

  override render(): unknown {
    const meta = this.parsed?.metadata
    const isMobile = window.innerWidth <= 600

    return html`
      <md-dialog @closed=${this.handleClose} ?fullscreen=${isMobile}>
        <div slot="headline">Import</div>
        <div slot="content">
          <div class="content">
            ${this.errorMessage
              ? html` <div class="error-message">${this.errorMessage}</div> `
              : ''}
            ${meta
              ? html`
                  <!-- Name -->
                  <md-outlined-text-field
                    label="Name"
                    .value=${this.layerName}
                    @input=${(e: Event): void => {
                      this.layerName = (e.target as MdOutlinedTextField).value
                    }}
                  ></md-outlined-text-field>

                  <!-- Destination mode -->
                  <div class="mode-row">
                    <button
                      class="mode-btn ${this.importMode === 'layer' ? 'active' : ''}"
                      ?disabled=${!!this.contextPlanId}
                      @click=${(): void => {
                        this.importMode = 'layer'
                      }}
                    >
                      <span class="material-icons">layers</span>
                      Add to layer
                    </button>
                    <button
                      class="mode-btn ${this.importMode === 'existing-plan' ? 'active' : ''}"
                      ?disabled=${this.plans.length === 0}
                      @click=${(): void => {
                        this.importMode = 'existing-plan'
                      }}
                    >
                      <span class="material-icons">map</span>
                      Existing plan
                    </button>
                    <button
                      class="mode-btn ${this.importMode === 'new-plan' ? 'active' : ''}"
                      ?disabled=${!!this.contextPlanId}
                      @click=${(): void => {
                        this.importMode = 'new-plan'
                      }}
                    >
                      <span class="material-icons">add_circle</span>
                      New plan
                    </button>
                  </div>

                  <!-- Mode-specific detail -->
                  <div class="mode-detail">${this.renderModeDetail()}</div>

                  <!-- Import options -->
                  ${this.renderImportOptions()}

                  <!-- Map preview -->
                  <div class="preview-map">
                    <div class="preview-map-container" ${ref(this.previewMapRef)}></div>
                  </div>

                  <!-- Metadata -->
                  <div class="metadata-summary">
                    ${meta.routeCount > 0
                      ? html`
                          <div class="meta-item">
                            <span class="meta-label">Routes</span>
                            <span class="meta-value">${meta.routeCount}</span>
                          </div>
                        `
                      : ''}
                    ${meta.trackCount > 0
                      ? html`
                          <div class="meta-item">
                            <span class="meta-label"
                              >${meta.sourceFormat === 'gpx' ? 'Tracks' : 'Lines'}</span
                            >
                            <span class="meta-value">${meta.trackCount}</span>
                          </div>
                        `
                      : ''}
                    ${meta.waypointCount > 0
                      ? html`
                          <div class="meta-item">
                            <span class="meta-label"
                              >${meta.sourceFormat === 'gpx' ? 'Waypoints' : 'Points'}</span
                            >
                            <span class="meta-value">${meta.waypointCount}</span>
                          </div>
                        `
                      : ''}
                    ${meta.totalDistanceKm > 0
                      ? html`
                          <div class="meta-item">
                            <span class="meta-label">Distance</span>
                            <span class="meta-value">${meta.totalDistanceKm} km</span>
                          </div>
                        `
                      : ''}
                    ${meta.totalPoints > 0
                      ? html`
                          <div class="meta-item">
                            <span class="meta-label">Points</span>
                            <span class="meta-value">${meta.totalPoints.toLocaleString()}</span>
                          </div>
                        `
                      : ''}
                    ${meta.elevationGain !== undefined
                      ? html`
                          <div class="meta-item">
                            <span class="meta-label">Gain</span>
                            <span class="meta-value">${meta.elevationGain}m</span>
                          </div>
                        `
                      : ''}
                    ${meta.elevationLoss !== undefined
                      ? html`
                          <div class="meta-item">
                            <span class="meta-label">Loss</span>
                            <span class="meta-value">${meta.elevationLoss}m</span>
                          </div>
                        `
                      : ''}
                  </div>
                `
              : ''}
          </div>
        </div>
        <div slot="actions">
          <md-text-button @click=${(): void => this.close('cancel')}>Cancel</md-text-button>
          <md-filled-button
            ?disabled=${!this.canImport()}
            @click=${(): void => this.close('import')}
            >Import</md-filled-button
          >
        </div>
      </md-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'import-dialog': ImportDialog
  }
}
